import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, CheckCircle, Clock, Calendar, UserCheck, MessageCircle, ChevronDown, ChevronUp, Search, AlertTriangle, Activity, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const FollowUpPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("operations");
  
  // Operations follow-up
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(15);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  // Missed sessions
  const [missedPatients, setMissedPatients] = useState([]);
  const [missedLoading, setMissedLoading] = useState(false);
  const [missedDays, setMissedDays] = useState(30);

  const fetchOperations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/patients/followup/recent-operations?days=${days}`, token);
      setPatients(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token, days]);

  const fetchMissed = useCallback(async () => {
    setMissedLoading(true);
    try {
      const res = await api.get(`/patients/followup/missed-sessions?days=${missedDays}`, token);
      setMissedPatients(res.data);
    } catch (err) { console.error(err); }
    finally { setMissedLoading(false); }
  }, [token, missedDays]);

  useEffect(() => { fetchOperations(); }, [fetchOperations]);
  useEffect(() => { if (activeTab === "missed") fetchMissed(); }, [activeTab, fetchMissed]);

  const dismissPatient = async (patientId, patientName) => {
    if (!window.confirm(`هل أنت متأكد من إزالة "${patientName}" من قائمة المتأخرين؟\n(لن يظهر مرة أخرى في هذه القائمة)`)) return;
    try {
      await api.put(`/patients/${patientId}/dismiss-sessions`, {}, token);
      toast.success("تم إزالة المريض من القائمة");
      fetchMissed();
    } catch (err) { console.error(err); toast.error("خطأ في الإزالة"); }
  };

  const markContacted = async (patientId) => {
    try {
      await api.post(`/patients/${patientId}/followup-log`, {}, token);
      toast.success("تم تسجيل المتابعة ✅");
      fetchOperations();
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  const filtered = patients.filter(p => 
    !search || p.name?.includes(search) || (p.last_name || "").includes(search) || (p.phone || "").includes(search)
  );
  const contacted = filtered.filter(p => p.contacted_today);
  const notContacted = filtered.filter(p => !p.contacted_today);

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="animate-fade-in" data-testid="followup-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-white" />
            </div>
            متابعة المرضى
          </h1>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setActiveTab("operations")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === "operations" ? "bg-orange-500 text-white shadow-lg" : "bg-gray-100 text-gray-600"}`}>
          <Calendar className="w-4 h-4" /> متابعة بعد العملية
        </button>
        <button onClick={() => setActiveTab("missed")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === "missed" ? "bg-red-500 text-white shadow-lg" : "bg-gray-100 text-gray-600"}`}>
          <AlertTriangle className="w-4 h-4" /> متأخرين عن الجلسات
        </button>
      </div>

      {/* ===== OPERATIONS TAB ===== */}
      {activeTab === "operations" && (
        <div>
          <div className="flex gap-2 mb-4">
            {[7, 15, 30].map(d => (
              <button key={d} onClick={() => setDays(d)} className={`px-4 py-2 rounded-xl text-sm font-bold ${days === d ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600"}`}>{d} يوم</button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-2xl border p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{filtered.length}</p>
              <p className="text-xs text-gray-500">إجمالي</p>
            </div>
            <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{contacted.length}</p>
              <p className="text-xs text-emerald-600">تم التواصل اليوم ✅</p>
            </div>
            <div className="bg-red-50 rounded-2xl border border-red-200 p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{notContacted.length}</p>
              <p className="text-xs text-red-600">بانتظار ⏳</p>
            </div>
          </div>

          <div className="relative mb-4">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="ابحث..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pr-10 pl-4 py-2.5 rounded-xl border bg-white focus:border-orange-400 focus:outline-none text-sm" />
          </div>

          {loading ? <p className="text-center py-8 text-gray-400">جاري التحميل...</p> : (
            <>
              {notContacted.map(p => (
                <div key={p.id} className="bg-white rounded-xl border-2 border-red-100 mb-2 overflow-hidden hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white font-bold text-sm">{p.days_since_operation}</div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm cursor-pointer hover:text-purple-600" onClick={(e) => { e.stopPropagation(); navigate(`/patients/${p.id}`); }}>{p.name} {p.last_name || ""}</h4>
                        <p className="text-xs text-gray-500">العملية: {p.transplant_date} • قبل {p.days_since_operation} يوم</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`tel:${p.phone}`} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"><Phone className="w-4 h-4" /></a>
                      <a href={`https://wa.me/${(p.phone || "").replace(/^0/, "964")}`} target="_blank" rel="noreferrer" className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"><MessageCircle className="w-4 h-4" /></a>
                      <button onClick={() => markContacted(p.id)} className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" /> تم التواصل
                      </button>
                    </div>
                  </div>
                  {expandedId === p.id && (
                    <div className="border-t px-4 py-3 bg-gray-50 text-xs space-y-1">
                      <p><span className="text-gray-500">الهاتف:</span> {p.phone}</p>
                      {p.followup_log?.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="font-bold text-gray-500 mb-1">سجل المتابعة ({p.followup_log.length} مرة):</p>
                          {p.followup_log.map((log, i) => <p key={`log-${i}`} className="text-gray-600">{fmtDate(log.date)} - {log.by}</p>)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {contacted.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-bold text-emerald-700 mb-2 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> تم التواصل اليوم ({contacted.length})</h3>
                  {contacted.map(p => (
                    <div key={p.id} className="bg-emerald-50 rounded-xl border border-emerald-200 p-3 mb-2 flex items-center justify-between opacity-75">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">✓</div>
                        <div>
                          <h4 className="font-bold text-gray-700 text-sm cursor-pointer hover:text-purple-600" onClick={() => navigate(`/patients/${p.id}`)}>{p.name} {p.last_name || ""}</h4>
                          <p className="text-xs text-gray-500">قبل {p.days_since_operation} يوم • {p.followup_log?.length || 0} مرة تواصل</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {filtered.length === 0 && <p className="text-center py-12 text-gray-400">لا يوجد مرضى بعمليات خلال آخر {days} يوم</p>}
            </>
          )}
        </div>
      )}

      {/* ===== MISSED SESSIONS TAB ===== */}
      {activeTab === "missed" && (
        <div>
          <div className="flex gap-2 mb-4">
            {[14, 30, 60, 90].map(d => (
              <button key={d} onClick={() => setMissedDays(d)} className={`px-4 py-2 rounded-xl text-sm font-bold ${missedDays === d ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600"}`}>{d} يوم</button>
            ))}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4">
            <p className="text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 inline ml-1" />
              المرضى اللي عندهم جلسات باقية ولم يزوروا العيادة خلال <strong>{missedDays} يوم</strong> أو أكثر
            </p>
          </div>

          {missedLoading ? <p className="text-center py-8 text-gray-400">جاري التحميل...</p> : missedPatients.length === 0 ? (
            <p className="text-center py-12 text-gray-400">لا يوجد مرضى متأخرين عن الجلسات</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-500 mb-2">{missedPatients.length} مريض متأخر</p>
              {missedPatients.map(p => (
                <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white font-bold">
                        {p.days_since_last_session}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 cursor-pointer hover:text-purple-600" onClick={() => navigate(`/patients/${p.id}`)}>{p.name} {p.last_name || ""}</h4>
                        {p.phone && <p className="text-xs text-blue-600 font-medium direction-ltr">{p.phone}</p>}
                        <p className="text-xs text-gray-500">آخر جلسة: {p.last_session_date} • قبل <span className="text-red-600 font-bold">{p.days_since_last_session} يوم</span></p>
                        <div className="flex gap-2 mt-1">
                          {p.plasma_remaining > 0 && <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">بلازما: {p.plasma_remaining} باقي</span>}
                          {p.meso_remaining > 0 && <span className="text-[10px] bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full font-bold">ميزو: {p.meso_remaining} باقي</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`tel:${p.phone}`} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"><Phone className="w-4 h-4" /></a>
                      <a href={`https://wa.me/${(p.phone || "").replace(/^0/, "964")}`} target="_blank" rel="noreferrer" className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"><MessageCircle className="w-4 h-4" /></a>
                      <button 
                        onClick={() => dismissPatient(p.id, `${p.name} ${p.last_name || ""}`)}
                        className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-bold hover:bg-red-50 hover:text-red-600 flex items-center gap-1 transition-colors"
                        data-testid={`dismiss-patient-${p.id}`}
                        title="إزالة من القائمة - لا يريد إكمال الجلسات"
                      >
                        <XCircle className="w-3.5 h-3.5" /> إزالة
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FollowUpPage;
